import {
  RequestError,
  isRetryable,
  isUnauthorized,
  isUnexpectedError,
} from '@cascade/core/errors';
import type { TErrorContext } from '@cascade/core/errors';
import type {
  TQueryErrorHandlers,
  TQueryKey,
  TQueryLoadSource,
  TQueryLoadState,
  TQueryOperation,
  TQueryPayload,
  TQueryScope,
} from './Query.types.ts';

const MAX_RETRY_COUNT = 3;
const BASE_RETRY_DELAY = 1_000;
const MAX_RETRY_DELAY = 30_000;

export const createQueryKey = <
  TScope extends TQueryScope,
  TOperation extends TQueryOperation<TScope>,
>(
  scope: TScope,
  operation: TOperation,
  ...payload: TQueryPayload<TScope, TOperation>
): TQueryKey<TScope, TOperation> => [scope, operation, ...payload] as const;

export const shouldRetry = (failureCount: number, error: Error): boolean =>
  isRetryable(error) && failureCount < MAX_RETRY_COUNT;

export const getRetryDelay = (failureCount: number, error: Error): number => {
  if (
    error instanceof RequestError &&
    error.detail.kind === 'server' &&
    error.detail.retryAfter !== undefined
  ) {
    return error.detail.retryAfter;
  }

  return Math.min(BASE_RETRY_DELAY * 2 ** failureCount, MAX_RETRY_DELAY);
};

export const reportQueryError = (
  error: Error,
  context: TErrorContext,
  { reporter, onUnauthenticated }: TQueryErrorHandlers,
): void => {
  if (isUnauthorized(error)) {
    onUnauthenticated(error);

    return;
  }

  if (isUnexpectedError(error)) {
    reporter(error, context);
  }
};

export const getQueryLoadState = ({
  isLoading,
  isLoadingError,
  isPending,
  isPaused,
  isPlaceholderData,
  error,
}: TQueryLoadSource): TQueryLoadState => ({
  isLoading: isLoading || (isPending && isPaused) || isPlaceholderData,
  loadError: isLoadingError ? (error ?? undefined) : undefined,
});
